/**
 * Logger Utility
 *
 * Production-safe logging that only outputs in development mode.
 * Prevents sensitive information leakage and reduces console noise in production.
 *
 * @example
 * ```ts
 * import { logger } from '@/lib/logger';
 *
 * logger.debug('User logged in', { uid: user.uid });
 * logger.info('Data loaded successfully');
 * logger.warn('Slow API response', { duration: 5000 });
 * logger.error('Failed to save', error);
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDev = import.meta.env.DEV;
  private appName = 'EtchAre';

  /**
   * Format timestamp for logs
   */
  private getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Format log message with context
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = this.getTimestamp();
    const prefix = `[${timestamp}] [${this.appName}] [${level.toUpperCase()}]`;

    if (context && Object.keys(context).length > 0) {
      return `${prefix} ${message}`;
    }

    return `${prefix} ${message}`;
  }

  /**
   * Debug-level logging (development only)
   * Use for detailed diagnostic information
   */
  debug(message: string, context?: LogContext) {
    if (this.isDev) {
      const formatted = this.formatMessage('debug', message, context);
      if (context) {
        console.log(formatted, context);
      } else {
        console.log(formatted);
      }
    }
  }

  /**
   * Info-level logging (development only)
   * Use for general informational messages
   */
  info(message: string, context?: LogContext) {
    if (this.isDev) {
      const formatted = this.formatMessage('info', message, context);
      if (context) {
        console.info(formatted, context);
      } else {
        console.info(formatted);
      }
    }
  }

  /**
   * Warning-level logging (all environments)
   * Use for potentially harmful situations
   */
  warn(message: string, context?: LogContext) {
    const formatted = this.formatMessage('warn', message, context);
    if (context) {
      console.warn(formatted, context);
    } else {
      console.warn(formatted);
    }
  }

  /**
   * Error-level logging (all environments)
   * Use for error events that might still allow the app to continue
   */
  error(message: string, error?: Error | unknown, context?: LogContext) {
    const formatted = this.formatMessage('error', message, context);

    if (error instanceof Error) {
      console.error(formatted, {
        message: error.message,
        stack: error.stack,
        ...context,
      });
    } else if (error) {
      console.error(formatted, { error, ...context });
    } else {
      console.error(formatted, context);
    }
  }

  /**
   * Log performance metrics (development only)
   */
  perf(label: string, duration: number) {
    if (this.isDev) {
      console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
    }
  }

  /**
   * Create a timer for performance measurement
   */
  time(label: string) {
    if (this.isDev) {
      console.time(label);
    }
  }

  /**
   * End a timer and log the duration
   */
  timeEnd(label: string) {
    if (this.isDev) {
      console.timeEnd(label);
    }
  }

  /**
   * Group related log messages (development only)
   */
  group(label: string) {
    if (this.isDev) {
      console.group(label);
    }
  }

  /**
   * End a log group
   */
  groupEnd() {
    if (this.isDev) {
      console.groupEnd();
    }
  }

  /**
   * Log a table (development only)
   * Useful for debugging arrays of objects
   */
  table(data: unknown) {
    if (this.isDev) {
      console.table(data);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export default for convenience
export default logger;
